import { Component } from '@theme/component';
import { debounce, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} activeOverflowItem - The overflow item shown when the More trigger is active.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /** @type {ResizeObserver | null} */
  #submenuResizeObserver = null;

  /** @type {WeakMap<HTMLElement, number>} */
  #submenuHeights = new WeakMap();

  /** @type {Map<HTMLElement, ReturnType<typeof setTimeout>>} */
  #submenuInactiveTimers = new Map();

  /** @type {number | undefined} */
  #submenuMeasureFrame;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  connectedCallback() {
    super.connectedCallback();

    const animationSpeed = getComputedStyle(this).getPropertyValue('--rift-header-submenu-animation-speed').trim();
    if (animationSpeed) {
      this.headerComponent?.style.setProperty('--submenu-animation-speed', animationSpeed);
    }

    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.addEventListener('pointerover', this.#onFlyoutPointerOver);
    this.addEventListener('pointerout', this.#onFlyoutPointerOut);
    this.addEventListener('focusin', this.#onFlyoutFocusIn);
    this.addEventListener('focusout', this.#onFlyoutFocusOut);
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('scroll', this.#onDropdownScroll, true);
    this.#syncTabletFlyouts();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('pointerover', this.#onFlyoutPointerOver);
    this.removeEventListener('pointerout', this.#onFlyoutPointerOut);
    this.removeEventListener('focusin', this.#onFlyoutFocusIn);
    this.removeEventListener('focusout', this.#onFlyoutFocusOut);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('scroll', this.#onDropdownScroll, true);
    this.#cleanupResizeObserver();
    if (this.#submenuMeasureFrame) cancelAnimationFrame(this.#submenuMeasureFrame);
    this.#submenuInactiveTimers.forEach((timer) => clearTimeout(timer));
    this.#submenuInactiveTimers.clear();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    this.#closeAllFlyouts();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    this.#submenuHeights = new WeakMap();
    this.#closeAllFlyouts();
    this.#syncTabletFlyouts();

    const activeItem = this.#state.activeItem;
    const submenu = findSubmenu(activeItem);
    if (!activeItem || !submenu) return;

    if (isCompactDropdown(submenu) && isDefaultMenuItem(activeItem)) {
      this.#positionCompactDropdown(activeItem, submenu);
      return;
    }

    this.#queueSubmenuMeasurement(submenu);
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeOverflowItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  /**
   * Find the first overflowing menu item shown by the More trigger.
   * @returns {HTMLElement | null}
   */
  #getFirstOverflowMenuItem() {
    const menuItem = this.refs.overflowMenu?.querySelector('[slot="overflow"] [ref="menuitem"]');
    return menuItem instanceof HTMLElement ? menuItem : null;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const isMoreTrigger = event.target.slot === 'more';
    const item = findMenuItem(event.target);
    const overflowItem = isMoreTrigger ? this.#getFirstOverflowMenuItem() : null;

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    const previouslyActiveOverflowItem = this.#state.activeOverflowItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }
    if (previouslyActiveOverflowItem && previouslyActiveOverflowItem !== previouslyActiveItem) {
      previouslyActiveOverflowItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.#state.activeOverflowItem = overflowItem;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    if (overflowItem && overflowItem !== item) {
      overflowItem.ariaExpanded = 'true';
    }

    const overflowItemSubmenu = isMoreTrigger ? findSubmenu(overflowItem) : null;
    let submenu = findSubmenu(item) || (overflowItemSubmenu ? this.overflowMenu : null);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const previousSubmenu = findSubmenu(previouslyActiveItem);
    if (previousSubmenu && previousSubmenu !== submenu) {
      this.#scheduleSubmenuInactive(previousSubmenu);
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active for content-visibility optimization
      this.#cancelSubmenuInactive(submenu);
      submenu.dataset.active = '';
      this.#preloadSubmenuImages(submenu);
    }

    const compactDropdown = Boolean(submenu && isDefaultSlot && isCompactDropdown(submenu));
    if (compactDropdown && submenu) {
      this.#cleanupResizeObserver();
      this.#positionCompactDropdown(item, submenu);
      this.headerComponent.style.setProperty('--submenu-height', '0px');
      this.#setFullOpenHeaderHeight(0, 0);
      this.style.setProperty('--submenu-opacity', '1');
      this.#startPointerTracking(item, previouslyActiveItem);
      return;
    }

    let finalHeight = submenu ? (this.#submenuHeights.get(submenu) ?? submenu.offsetHeight) : 0;
    if (submenu && finalHeight > 0) {
      this.#submenuHeights.set(submenu, finalHeight);
    }

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
    if (submenu && isDefaultSlot) {
      this.#observeSubmenu(submenu);
    } else {
      this.#cleanupResizeObserver();
    }
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Element && Boolean(event.relatedTarget.closest('[slot="overflow"]'));

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    this.#cleanupResizeObserver();
    this.#closeAllFlyouts();

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item) || (item.closest('[slot="more"]') ? this.overflowMenu : null);
    const activeOverflowItem = this.#state.activeOverflowItem;

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.#state.activeOverflowItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    if (activeOverflowItem && activeOverflowItem !== item) {
      activeOverflowItem.ariaExpanded = 'false';
    }

    if (submenu) {
      this.#scheduleSubmenuInactive(submenu);
    }
  };

  /**
   * Observe an open mega menu and update its cached height after hydration or image sizing.
   * @param {HTMLElement} submenu
   */
  #observeSubmenu(submenu) {
    this.#cleanupResizeObserver();
    this.#submenuResizeObserver = new ResizeObserver(() => {
      this.#preloadSubmenuImages(submenu);
      this.#queueSubmenuMeasurement(submenu);
    });
    this.#submenuResizeObserver.observe(submenu);
  }

  /**
   * Batch submenu measurement into one animation frame.
   * @param {HTMLElement} submenu
   */
  #queueSubmenuMeasurement(submenu) {
    if (this.#submenuMeasureFrame) cancelAnimationFrame(this.#submenuMeasureFrame);
    this.#submenuMeasureFrame = requestAnimationFrame(() => {
      this.#submenuMeasureFrame = undefined;
      if (findSubmenu(this.#state.activeItem) !== submenu || isCompactDropdown(submenu)) return;

      const height = submenu.offsetHeight;
      if (height <= 0 || this.#submenuHeights.get(submenu) === height) return;

      this.#submenuHeights.set(submenu, height);
      const headerVisibleHeight = this.#getHeaderVisibleHeight();
      this.headerComponent?.style.setProperty('--submenu-height', `${height}px`);
      this.#setFullOpenHeaderHeight(height, headerVisibleHeight);
    });
  }

  /**
   * Keep a submenu renderable until its closing transition completes.
   * @param {HTMLElement} submenu
   */
  #scheduleSubmenuInactive(submenu) {
    this.#cancelSubmenuInactive(submenu);
    submenu.dataset.riftClosing = '';
    const timer = setTimeout(() => {
      delete submenu.dataset.active;
      delete submenu.dataset.riftClosing;
      this.#submenuInactiveTimers.delete(submenu);
    }, this.#getAnimationDuration());
    this.#submenuInactiveTimers.set(submenu, timer);
  }

  /** @param {HTMLElement} submenu */
  #cancelSubmenuInactive(submenu) {
    const timer = this.#submenuInactiveTimers.get(submenu);
    if (timer) clearTimeout(timer);
    this.#submenuInactiveTimers.delete(submenu);
    delete submenu.dataset.riftClosing;
  }

  #getAnimationDuration() {
    const value = getComputedStyle(this).getPropertyValue('--rift-header-submenu-animation-speed').trim();
    if (value.endsWith('ms')) return Number.parseFloat(value) || 220;
    if (value.endsWith('s')) return (Number.parseFloat(value) || 0.22) * 1000;
    return 220;
  }

  /**
   * Place the compact panel under its trigger while keeping it inside the viewport.
   * @param {HTMLElement} item
   * @param {HTMLElement} submenu
   */
  #positionCompactDropdown(item, submenu) {
    const panel = submenu.querySelector('.rift-header-dropdown__panel');
    if (!(panel instanceof HTMLElement)) return;

    const submenuRect = submenu.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const panelWidth = panel.offsetWidth;
    const safeInset = 16;
    const isRtl = getComputedStyle(this).direction === 'rtl';
    const rawOffset = isRtl ? submenuRect.right - itemRect.right : itemRect.left - submenuRect.left;
    const maxOffset = Math.max(safeInset, submenuRect.width - panelWidth - safeInset);
    const offset = Math.min(Math.max(rawOffset, safeInset), maxOffset);

    submenu.style.setProperty('--rift-header-dropdown-offset', `${offset}px`);
  }

  #onFlyoutPointerOver = (event) => {
    if (!(event.target instanceof Element) || window.innerWidth < 990) return;
    const item = event.target.closest('[data-rift-header-flyout-item]');
    if (item instanceof HTMLElement && this.contains(item)) this.#openFlyout(item);
  };

  #onFlyoutPointerOut = (event) => {
    if (!(event.target instanceof Element) || window.innerWidth < 990) return;
    const item = event.target.closest('[data-rift-header-flyout-item]');
    if (!(item instanceof HTMLElement)) return;
    if (event.relatedTarget instanceof Node && item.contains(event.relatedTarget)) return;
    this.#closeFlyout(item);
  };

  #onFlyoutFocusIn = (event) => {
    if (!(event.target instanceof Element) || window.innerWidth < 990) return;
    const item = event.target.closest('[data-rift-header-flyout-item]');
    if (item instanceof HTMLElement && this.contains(item)) this.#openFlyout(item);
  };

  #onFlyoutFocusOut = (event) => {
    if (!(event.target instanceof Element) || window.innerWidth < 990) return;
    const item = event.target.closest('[data-rift-header-flyout-item]');
    if (!(item instanceof HTMLElement)) return;
    if (event.relatedTarget instanceof Node && item.contains(event.relatedTarget)) return;
    this.#closeFlyout(item);
  };

  #onDropdownScroll = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('.rift-header-dropdown__panel')) return;
    this.#closeAllFlyouts();
  };

  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    const openFlyout = [...this.querySelectorAll('[data-rift-header-flyout-item][data-rift-flyout-open="true"]')].pop();
    if (openFlyout instanceof HTMLElement && window.innerWidth >= 990) {
      const trigger = openFlyout.querySelector('[data-rift-header-flyout-trigger]');
      this.#closeFlyout(openFlyout);
      if (trigger instanceof HTMLElement) trigger.focus();
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const activeItem = this.#state.activeItem;
    if (!activeItem) return;
    this.#deactivate(activeItem);
    activeItem.focus();
    event.preventDefault();
    event.stopPropagation();
  };

  /** @param {HTMLElement} item */
  #openFlyout(item) {
    if (item.dataset.riftFlyoutOpen === 'true') return;

    const list = item.parentElement;
    list?.querySelectorAll(':scope > [data-rift-header-flyout-item]').forEach((candidate) => {
      if (candidate instanceof HTMLElement && candidate !== item) this.#closeFlyout(candidate);
    });

    const trigger = item.querySelector(':scope > [data-rift-header-flyout-trigger]');
    const flyout = item.querySelector(':scope > [data-rift-header-flyout]');
    if (!(trigger instanceof HTMLElement) || !(flyout instanceof HTMLElement)) return;

    item.dataset.riftFlyoutOpen = 'true';
    trigger.ariaExpanded = 'true';
    this.#positionFlyout(item, flyout);
  }

  /**
   * Position a third-level flyout on the side with the most usable space.
   * @param {HTMLElement} item
   * @param {HTMLElement} flyout
   */
  #positionFlyout(item, flyout) {
    if (window.innerWidth < 990) return;

    const itemRect = item.getBoundingClientRect();
    const flyoutWidth = flyout.offsetWidth;
    const flyoutHeight = flyout.offsetHeight;
    const safeInset = 16;
    // Keep the flyout flush with its parent item so pointer travel has no dead zone.
    const gap = 0;
    const isRtl = getComputedStyle(this).direction === 'rtl';
    const endPosition = itemRect.right + gap;
    const startPosition = itemRect.left - flyoutWidth - gap;
    const preferredPosition = isRtl ? startPosition : endPosition;
    const alternatePosition = isRtl ? endPosition : startPosition;
    const preferredFits = preferredPosition >= safeInset && preferredPosition + flyoutWidth <= window.innerWidth - safeInset;
    const left = Math.min(
      Math.max(preferredFits ? preferredPosition : alternatePosition, safeInset),
      Math.max(safeInset, window.innerWidth - flyoutWidth - safeInset)
    );
    const top = Math.min(
      Math.max(itemRect.top, safeInset),
      Math.max(safeInset, window.innerHeight - flyoutHeight - safeInset)
    );

    flyout.style.setProperty('--rift-header-flyout-left', `${left}px`);
    flyout.style.setProperty('--rift-header-flyout-top', `${top}px`);
    flyout.style.setProperty('--rift-header-flyout-shift', `${left < itemRect.left ? 6 : -6}px`);
  }

  /** @param {HTMLElement} item */
  #closeFlyout(item) {
    const trigger = item.querySelector(':scope > [data-rift-header-flyout-trigger]');
    if (trigger instanceof HTMLElement) trigger.ariaExpanded = 'false';
    delete item.dataset.riftFlyoutOpen;
  }

  #closeAllFlyouts() {
    this.querySelectorAll('[data-rift-header-flyout-item]').forEach((item) => {
      if (item instanceof HTMLElement) this.#closeFlyout(item);
    });
  }

  #syncTabletFlyouts() {
    const expanded = window.innerWidth >= 750 && window.innerWidth < 990;
    this.querySelectorAll('[data-rift-header-flyout-item]').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      const trigger = item.querySelector(':scope > [data-rift-header-flyout-trigger]');
      if (trigger instanceof HTMLElement) trigger.ariaExpanded = expanded.toString();
      if (expanded) item.dataset.riftFlyoutOpen = 'true';
      else delete item.dataset.riftFlyoutOpen;
    });
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /** @param {HTMLElement} submenu */
  #preloadSubmenuImages(submenu) {
    submenu.querySelectorAll('img[loading="lazy"]').forEach((image) => image.removeAttribute('loading'));
  }

  #cleanupResizeObserver() {
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/** @param {HTMLElement} submenu */
function isCompactDropdown(submenu) {
  return submenu.dataset.riftSubmenuLayout === 'dropdown';
}

/** @param {HTMLElement} item */
function isDefaultMenuItem(item) {
  return item.parentElement?.slot === '';
}
